import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ChartNode} from 'charts/ChartTypes';
import {GetChart, GetChartVariables} from 'charts/network/__generated/GetChart';
import {GET_CHART_QUERY} from 'charts/network/GetChart';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MakeFieldsOptional} from 'util/Types';

import {SecurePlotlyChart} from './SecurePlotlyChart';

export type ChartContentChart = MakeFieldsOptional<
  Pick<ChartNode, 'id' | 'libraryConfig' | 'rasterDownloadUrl' | 'rasterExternalFileId'>,
  'rasterDownloadUrl' | 'rasterExternalFileId'
>;

export interface ChartContentProps {
  chart: ChartContentChart;
  renderStaticChart?: boolean;
  /**
   * Called when multiple points are selected via lasso or box selection.
   */
  onPointsSelected?: (rowIndices: Array<number>) => void;
  /**
   * Set of selected row indices to highlight in the chart
   */
  selectedRowIndices?: Set<number>;
  /**
   * Whether to allow lasso selection mode on this chart.
   */
  allowLassoSelection?: boolean;
}

export default function ChartContent({
  chart: {id: chartId, libraryConfig, rasterDownloadUrl, rasterExternalFileId},
  renderStaticChart = false,
  onPointsSelected,
  selectedRowIndices,
  allowLassoSelection = false,
}: ChartContentProps): JSX.Element {
  const [isUnloaded, setIsUnloaded] = useState<boolean>(false);
  const handleChartForceUnloaded = useCallback(() => {
    setIsUnloaded(true);
  }, []);
  assertAndContinue(
    !renderStaticChart || (renderStaticChart && rasterDownloadUrl != null),
    'Statically renderable chart is missing raster download url'
  );

  return (
    <div className={css(styles.contentWrapper)}>
      {isUnloaded && (
        <div style={{padding: 8}}>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="Interactivity is disabled, due to a large number of charts on the page."
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Enable interactivity',
              actionOnClick: () => {
                setIsUnloaded(false);
              },
            }}
          />
        </div>
      )}
      {(renderStaticChart || isUnloaded) && rasterDownloadUrl != null && rasterExternalFileId != null ? (
        <SignedExtFileURLProxy
          externalFileId={rasterExternalFileId}
          url={rasterDownloadUrl}
          renderWithSignedURL={(signedURL) => {
            if (signedURL === undefined) {
              return <CenteredHorizontalThrobber />;
            } else if (signedURL == null) {
              return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading image." />;
            } else {
              return (
                <div className={css(styles.staticChart)}>
                  <img className={css(styles.image)} src={signedURL} />
                </div>
              );
            }
          }}
          owningEntityApiId={chartId}
        />
      ) : (
        isUnloaded || (
          <SecurePlotlyChart
            libraryConfig={libraryConfig}
            onChartForceUnloaded={handleChartForceUnloaded}
            onPointsSelected={onPointsSelected}
            selectedRowIndices={selectedRowIndices}
            allowLassoSelection={allowLassoSelection}
          />
        )
      )}
    </div>
  );
}
interface QueryingChartContentProps {
  chartId: string;
  renderStaticChart?: boolean;
}

export function QueryingChartContent({
  chartId,
  renderStaticChart = false,
}: QueryingChartContentProps): JSX.Element {
  return (
    <BenchlingQuery<GetChart, GetChartVariables>
      query={GET_CHART_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{chartId}}
    >
      {(data) =>
        isInaccessibleInfo(data.chart) ? (
          <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this chart." />
        ) : (
          <ChartContent chart={data.chart} renderStaticChart={renderStaticChart} />
        )
      }
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  contentWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  staticChart: {
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    overflow: 'hidden',
  },
});
